// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2023-05-02 21:19:07 +0200 (Tue, 02 May 2023) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 134382 $
//  Purpose   : register svg graphics
// =============================================================================
// ---Third-party imports
import {size, entries, has, includes, dict} from 'ldash'
import now from 'performance-now'
import {put} from 'redux-saga/effects'

// ---Framework imports
import log from 'uix/logging'
import {delay} from 'uix/sagas'
import {Actions} from 'uix/registries'

// ---Globals
export {Icons, VIcon, vicon} from './fonts'

export const Graphics = {}

export const isGraphicChunkLoaded = name => has(Graphics, name)

let index = svgs =>
  svgs.keys().reduce((images, path) => {
    images[path.slice(2, -4)] = svgs(path)
    return images
  }, {})

// #if process.env.UIX_CHUNKED === 'false'
// //~ import {types} from './types'
// //~ import {states} from './states'
// //~ import {connectors} from './connectors'
// //~ import {common} from './common'
// //~ import {controls, license, tree, icons} from './other'
// 
// //~ const imported = {types, states, connectors, controls, license, common, tree, icons}
// //~ for (let [name, data] of entries(imported)) {
// //~ Graphics[name] = index(data)
// //~ }
// #endif

export function* loadGraphicChunks(chunkNames, customize) {
  /* saga for graphic chunks loading
   */
  let loaded = {}

  let load = (importPromise, name, ts) =>
    importPromise
      .then(module => {
        try {
          let data = module.data || module[name] || []
          loaded[name] = index(data)
          if (size(loaded[name]) == 0) {
            log.error('graphic chunk %o does not expose any data', name)
            loaded[name] = false
          } else {
            log.chunk('loaded %s graphics from %o [%d ms]', size(loaded[name]), name, now() - ts)
          }
          if (customize[name]) {
            customize[name](loaded[name])
          }
        } catch (err) {
          log.exception(`error fetching graphic chunk ${name} %O`, err)
          loaded[name] = false
        }
      })
      .catch(err => {
        log.error('load failure for graphic chunk %o %O', name, err)
        loaded[name] = false
      })

  // one big other.svg chunk
  let OTHER = ['controls', 'license', 'common', 'tree', 'icons', 'web']

  let promises = []

  // #XXXIF process.env.UIX_CHUNKED !== 'true'
  for (let n of chunkNames) {
    if (n == 'types') {
      promises.push(load(import(/* webpackChunkName: 'types.svg' */ './types'), n, now()))
    } else if (n == 'states') {
      promises.push(load(import(/* webpackChunkName: 'states.svg' */ './states'), n, now()))
    } else if (n == 'connectors') {
      promises.push(load(import(/* webpackChunkName: 'connectors.svg' */ './connectors'), n, now()))
    } else if (n == 'common') {
      promises.push(load(import(/* webpackChunkName: 'common.svg' */ './common'), n, now()))
    } else if (includes(OTHER, n)) {
      promises.push(load(import(/* webpackChunkName: 'other.svg' */ './other'), n, now()))
    } else {
      log.error('unknown chunk name %o', n)
      continue
    }
  }
  // #XXXENDIF

  //~ log.debug('waiting for %o chunks', size(promises))
  while (size(loaded) < size(promises)) {
    yield delay(5)
  }

  let loadedSucc = entries(loaded).filter(e => e[1])

  yield put(Actions.setGraphicsLoaded(loadedSucc))

  for (let [name, data] of loadedSucc) {
    Graphics[name] = data // load errors are flagged as "false"
  }
  //~ log.debug('%o chunks loaded', size(loaded))
}
